/** API e stato server delle analisi AI sui titoli (SPEC-007). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export type AnalysisStatus = 'pending' | 'running' | 'completed' | 'failed';
export type AnalysisAssetType = 'stock' | 'crypto';
/**
 * `Review` non è un sesto livello della scala: l'analisi è arrivata in fondo ma
 * nella decisione finale non c'era un giudizio riconoscibile (SPEC-007.1). Il
 * report resta valido — il consiglio no. Va **spiegato** ovunque compaia:
 * vedi `RATING_HINT` in `analysis-rating.ts`.
 */
export type AnalysisRating = 'Buy' | 'Overweight' | 'Hold' | 'Underweight' | 'Sell' | 'Review';

export type AnalysisSummary = {
  id: string;
  status: AnalysisStatus;
  assetType: AnalysisAssetType;
  tradeDate: string;
  selectedAnalysts: string[];
  rating: AnalysisRating | null;
  executiveSummary: string | null;
  /** Estratti dal report per l'anteprima ("ultimo capitolo": la decisione finale). */
  investmentThesis: string | null;
  priceTarget: number | null;
  timeHorizon: string | null;
  llmProvider: string | null;
  llmDeepModel: string | null;
  llmQuickModel: string | null;
  /** Ultimo passo noto mentre l'analisi è pending/running. */
  progress: string | null;
  errorMessage: string | null;
  requestedAt: string;
  startedAt: string | null;
  completedAt: string | null;
  /** Origine della richiesta: manuale o trigger pre-apertura di mercato (SPEC-010 §8.4). */
  triggeredBy: 'manual' | 'market_open_trigger';
  marketBriefingId: string | null;
};

export type FullReport = {
  marketReport: string;
  sentimentReport: string;
  newsReport: string;
  fundamentalsReport: string;
  bullHistory: string;
  bearHistory: string;
  researchManagerDecision: string;
  traderPlan: string;
  riskAggressive: string;
  riskConservative: string;
  riskNeutral: string;
  riskManagerDecision: string;
  finalTradeDecision: string;
  investmentThesis: string;
  priceTarget: number | null;
  timeHorizon: string | null;
  durationMs: number;
  /** Il portafoglio come lo hanno letto gli agenti (F-170); null senza contesto o per le analisi precedenti. */
  portfolioContext?: string | null;
};

/**
 * Il portafoglio che il grafo aveva davanti quando ha deciso (F-170).
 *
 * ⚠️ Cambia cosa il giudizio SIGNIFICA: con una posizione gia' in mano, un
 * `Hold` e' «non aggiungere» e un `Buy` e' «aggiungere a quel che c'e'», non
 * un'opinione sul titolo in astratto. La pagina lo mostra accanto al giudizio
 * per questo. `null` = analisi senza contesto (nessun portafoglio in
 * auto-trading live, o analisi precedente): NON «libro vuoto».
 */
export type AnalysisPortfolioContext = {
  asOf: string | null;
  currency: string;
  cash: string | null;
  portfolios: Array<{ id: string; name: string; snapshotDate: string | null }>;
  position: { ticker: string; quantity: string; averagePrice: string | null; currency: string } | null;
  others: Array<{ ticker: string; quantity: string }>;
};

/** Movimento registrato come "azione intrapresa" a partire da questa analisi (SPEC-008 §7). */
export type LinkedTransaction = {
  id: string;
  txType: string;
  txDate: string;
  quantity: string | null;
  price: string | null;
  amount: string;
  currency: string;
  notes: string | null;
};

/** Livelli di prezzo riconosciuti nel report (F-068), in ordine di scala. */
export type PriceLevelKind =
  | 'stop_loss'
  | 'support'
  | 'entry'
  | 'current'
  | 'target'
  | 'resistance'
  | 'take_profit';

export type PriceLevel = {
  kind: PriceLevelKind;
  /** Nella valuta e nell'unità del report: per Londra sono pence. */
  value: string;
  /** Lo stesso importo nell'unità principale della valuta. */
  valueMajor: string;
  /** Nella valuta dell'utente; `null` se il cambio manca o la valuta non è affidabile. */
  valueBase: string | null;
  source: 'structured' | 'prose' | 'quote' | 'llm';
};

export type AnalysisPrices = {
  currency: string | null;
  /** `minor` = sottounità (i pence di Londra): l'importo va diviso per cento. */
  unit: 'major' | 'minor';
  confidence: 'high' | 'medium' | 'low';
  /** Perché la valuta è quella: mostrato all'utente, non solo registrato. */
  reason: string;
  baseCurrency: string;
  levels: PriceLevel[];
  timeHorizon: string | null;
  /** Quotazione con cui il report è stato confrontato, per poter verificare la scala. */
  referencePrice: string | null;
  referenceCurrency: string | null;
  referenceAt: string | null;
};

export type AnalysisDetail = AnalysisSummary & {
  fullReport: FullReport | null;
  completeReportMarkdown: string | null;
  instrument: { id: string; name: string; symbol: string | null; isin: string | null };
  /** Riepilogo e valutazione del connettore LLM (SPEC-008 §8), null se mai calcolati. */
  llmSummary: string | null;
  llmPortfolioComparison: string | null;
  linkedTransactions: LinkedTransaction[];
  /** Prezzi e valuta ricavati dal report (F-068). */
  prices: AnalysisPrices;
  portfolioContext: AnalysisPortfolioContext | null;
};

export type AnalysisQuota = { used: number; limit: number; resetAt: string };

const ACTIVE_STATUSES: AnalysisStatus[] = ['pending', 'running'];

export const aiAnalysisKeys = {
  quota: ['ai-analysis', 'quota'] as const,
  list: (instrumentId: string) => ['ai-analysis', 'list', instrumentId] as const,
  detail: (instrumentId: string, analysisId: string) => ['ai-analysis', 'detail', instrumentId, analysisId] as const,
};

export function useAiAnalysisQuota() {
  return useQuery({
    queryKey: aiAnalysisKeys.quota,
    queryFn: ({ signal }) => api.get<AnalysisQuota>('/ai-analysis/quota', signal),
  });
}

/** Polling leggero finché almeno un'analisi è pending/running (SPEC-007 §6). */
export function useInstrumentAnalyses(instrumentId: string) {
  return useQuery({
    queryKey: aiAnalysisKeys.list(instrumentId),
    queryFn: ({ signal }) => api.get<{ items: AnalysisSummary[] }>(`/instruments/${instrumentId}/analyses`, signal),
    refetchInterval: (query) => {
      const items = query.state.data?.items ?? [];
      return items.some((item) => ACTIVE_STATUSES.includes(item.status)) ? 10_000 : false;
    },
  });
}

export function useAnalysisDetail(instrumentId: string, analysisId: string, enabled: boolean) {
  return useQuery({
    queryKey: aiAnalysisKeys.detail(instrumentId, analysisId),
    queryFn: ({ signal }) => api.get<AnalysisDetail>(`/instruments/${instrumentId}/analyses/${analysisId}`, signal),
    enabled,
  });
}

export function useRequestAnalysis(instrumentId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<AnalysisSummary>(`/instruments/${instrumentId}/analyses`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: aiAnalysisKeys.list(instrumentId) });
      void queryClient.invalidateQueries({ queryKey: aiAnalysisKeys.quota });
    },
  });
}
